import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { GRID_CARD_TYPES } from '../constants';
import useNumGridCols from '../hooks/useNumGridCols';

import AssetCard from './AssetCard';
import GridDebug from './GridDebug';
import ProductCard from './ProductCard/ProductCard';

const GridCardRendererWrapper = styled.div<{
  $columnSpan: number;
  $rowSpan: number;
}>`
  grid-column: span ${({ $columnSpan }) => $columnSpan};
  grid-row: span ${({ $rowSpan }) => $rowSpan};
  width: 100%;
  min-width: 0;
  position: relative;
`;

const GridCardRenderer = ({
  item,
  isFirstProductCard,
  row,
  column,
  showIndexInfo = false,
  onActiveProductChange,
}) => {
  const numGridColumns = useNumGridCols();
  const columnSpan = item?.span?.columns || 1;
  const rowSpan = item?.span?.rows || 1;
  const gridIndex = row * numGridColumns + column;

  // Track the active variant so the debug overlay reflects whichever
  // swatch the user has selected — without it, the modal stays pinned
  // to the primary master product even after a swatch click.
  const [activeVariant, setActiveVariant] = useState(null);
  const handleActiveProductChange = useCallback(
    (matchedRelatedProduct) => {
      setActiveVariant(matchedRelatedProduct ?? null);
      if (onActiveProductChange) {
        onActiveProductChange();
      }
    },
    [onActiveProductChange]
  );

  // Related-product entries identify themselves by `masterProductId`, but
  // GridDebug (and its url resolvers) read `item.id`. Remap so the modal's
  // ID, edit URLs, and ES link reflect the variant.
  const debugItem = activeVariant
    ? {
        ...item,
        ...activeVariant,
        id: activeVariant.masterProductId ?? item?.id,
      }
    : item;

  return (
    <GridCardRendererWrapper $columnSpan={columnSpan} $rowSpan={rowSpan}>
      {showIndexInfo && (
        <GridDebug
          row={row}
          column={column}
          numGridColumns={numGridColumns}
          item={debugItem}
        />
      )}
      {item?.type === GRID_CARD_TYPES.ASSET ? (
        <AssetCard {...item} isFullRow={columnSpan === numGridColumns} />
      ) : (
        <ProductCard
          {...item}
          gridIndex={gridIndex}
          isFirstProductCard={isFirstProductCard}
          onActiveProductChange={handleActiveProductChange}
        />
      )}
    </GridCardRendererWrapper>
  );
};

GridCardRenderer.propTypes = {
  column: PropTypes.number.isRequired,
  isFirstProductCard: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    span: PropTypes.shape({
      rows: PropTypes.number,
      columns: PropTypes.number,
    }),
    options: PropTypes.shape({
      customVars: PropTypes.object,
    }),
  }).isRequired,
  onActiveProductChange: PropTypes.func,
  row: PropTypes.number.isRequired,
  showIndexInfo: PropTypes.bool,
  variant: PropTypes.string,
};

export default React.memo(GridCardRenderer);
